import { useInfiniteQuery as useReactQueryInfiniteQuery } from '@tanstack/react-query';
import type {
  GetNextPageParamFunction,
  QueryFunction,
  QueryFunctionContext,
  QueryKey,
  UseInfiniteQueryOptions as UseInfiniteQueryOptionsReactQuery,
} from '@tanstack/react-query';
import type { SetRequired } from 'type-fest';

import type { PaginatedResponse, PaginatedResponseWithTotal } from '../../shared/types/routes/pagination';

/**
 * Generic return type for query functions.
 */
export type QueryResponse<ResponseBody> = Response & {
  data?: ResponseBody;
};

//  Like the usual useQuery options, but marks the queryFn as required (it should always be provided).
type UseInfiniteQueryOptions<ResponseBody = unknown, ResponseError = Error, TData = ResponseBody> = SetRequired<
  Omit<
    UseInfiniteQueryOptionsReactQuery<
      QueryResponse<ResponseBody>,
      ResponseError,
      TData,
      QueryResponse<ResponseBody>
    >,
    'getNextPageParam'
  >,
  'queryFn'
>;

function isPaginatedWithTotal(
  response: PaginatedResponse<unknown> | PaginatedResponseWithTotal<unknown>,
): response is PaginatedResponseWithTotal<unknown> {
  return 'totalCount' in response;
}

/**
 * A method to extract the response data from a query request.
 * Note that errors will continue to bubble up to be thrown by React Query,
 * and should be handled in the consuming component.
 *
 * @param {() => Promise<{ data?: ResponseBody }>} request - The request to make.
 */
async function getResponseData<ResponseBody = unknown, TQueryKey extends QueryKey = QueryKey>(
  request: QueryFunction<QueryResponse<ResponseBody>, TQueryKey>,
  context: QueryFunctionContext<TQueryKey>, // This needs to be passed through to the request function, in case it is used.
): Promise<ResponseBody | undefined> {
  const response = await request(context);
  return response?.data ?? undefined;
}

/**
 * Wrapper around the default useInfiniteQuery hook that provides improved types.
 * Specifically, it will resolve the response.data and return that, so that
 * you don't need to read `data.data` in the consuming component.
 *
 * This is useful for infinitely loading lists, like Issue Revisions, since it does not handle
 * paginating starting from a page other than 1. If we extend it to handle that, it could be used
 * for paginated requests.
 */
export default function useInfiniteListQuery<
  ResponseBody extends PaginatedResponse<unknown>,
  ResponseError = Error,
  TData = ResponseBody,
>(options: UseInfiniteQueryOptions<ResponseBody, ResponseError, TData>, shouldParseResponseData = true) {
  const queryFn = (context: QueryFunctionContext) => {
    if (shouldParseResponseData) {
      return getResponseData<ResponseBody>(options.queryFn, context);
    }
    return options.queryFn(context);
  };

  const getNextPageParam = (lastPage: ResponseBody) => {
    // If we know the total count of results, we can determine if we've received all of them.
    if (isPaginatedWithTotal(lastPage) && lastPage.totalCount <= lastPage.page * lastPage.pageSize) {
      return undefined;
    }

    // We received less than the requested page size, so we're done.
    if (lastPage.results.length < lastPage.pageSize) {
      return undefined;
    }

    // Otherwise, we can just increment the page number.
    return lastPage.page + 1;
  };

  const queryOptions: UseInfiniteQueryOptionsReactQuery<QueryResponse<ResponseBody>, ResponseError, TData> = {
    ...options,
    //  TODO: Remove this cast when the types are fixed in react-query.
    queryFn: queryFn as unknown as QueryFunction<QueryResponse<ResponseBody>>,
    getNextPageParam: getNextPageParam as unknown as GetNextPageParamFunction<QueryResponse<ResponseBody>>,
    // This makes it so that the cache is cleared when the query is unmounted,
    // otherwise it will refetch all previously fetched pages when the query is remounted.
    cacheTime: 0,
  };
  return useReactQueryInfiniteQuery(queryOptions);
}
